'use client';

import { useEffect, useState } from 'react';
import { createAnalytics } from '@bedrock/analytics';
import { coreEvents, ecommerceEvents, formEvents } from '@bedrock/analytics/events';
import {
  useOutboundLink,
  usePageView,
  useScrollPercentage,
  useStandardClick,
} from '@bedrock/analytics/hooks';
import { allSchemas } from '@bedrock/analytics/schema';
import { useAnyRoadAnalytics } from 'lib/analytics/use-anyroad-analytics';
import { useOpenTableAnalytics } from 'lib/analytics/use-opentable-analytics';

const ECOMMERCE_BASE_INFO = {
  brand: 'Guinness',
  affiliation: 'Chicago',
};

const allEvents = {
  ...coreEvents,
  ...ecommerceEvents,
  ...formEvents,
};

if (typeof globalThis !== 'undefined') {
  (
    globalThis as typeof globalThis & {
      __analyticsQueueConfig?: { triggerEventName: string; enabled: boolean };
    }
  ).__analyticsQueueConfig = {
    triggerEventName: 'OneTrustLoaded',
    enabled: true,
  };
}

export const analytics = createAnalytics<typeof allEvents>({
  schemas: allSchemas,
  handlers: allEvents,
});

type AnalyticsTrackerProps = {
  contentType: string;
  locale: string;
};

const SITE_SECTION_MAP: Record<string, string> = {
  home: 'Home',
  about: 'About',
  visit: 'Visit',
  'private-events': 'Private Events',
  reservations: 'Reservations',
  food: 'Food',
  'tap-list': 'Tap List',
  faqs: 'FAQs',
  contact: 'Contact',
};

const SITE_SUBSECTION_MAP: Record<string, string> = {
  ourstory: 'Our Story',
  whoweare: 'Who We Are',
  careers: 'Careers',
  calendar: 'Calendar',
  experiences: 'Experiences',
};

const PAGE_TYPE_MAP: Record<string, string> = {
  home: 'Home',
  plp: 'PLP',
  pdp: 'PDP',
  content: 'Content',
  default: 'Content',
  '404': '404',
  notfound: '404',
  checkout: 'PLP',
};

const SECTION_OVERRIDES: Record<string, string> = {
  faqs: 'FAQs',
};

function capitalize(str: string): string {
  const override = SECTION_OVERRIDES[str.toLowerCase()];
  if (override) return override;
  return str
    .replaceAll(/(?<=[a-z])(?=[A-Z])/g, ' ')
    .replaceAll('-', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeMapKey(value: string): string {
  return value.replaceAll(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function getPageType(contentType: string, siteSection: string, siteSubsection?: string): string {
  const contentTypeKey = normalizeMapKey(contentType);

  if (siteSection === 'Home' && contentTypeKey === 'default') {
    return 'Home';
  }

  if (siteSubsection === 'Experiences') {
    return 'PLP';
  }

  return PAGE_TYPE_MAP[contentTypeKey] ?? 'Content';
}

function buildPageViewPayload(params: {
  locale: string;
  brand: string;
  region: string;
  siteType: string;
  pageType: string;
  siteSection?: string;
  siteSubsection?: string;
}) {
  const { locale, brand, region, siteType, pageType, siteSection, siteSubsection } = params;

  const shouldIncludeSiteHierarchy = pageType !== '404';

  return {
    page_locale: locale.split('-')[1]?.toUpperCase() || 'US',
    region,
    site_type: siteType,
    page_type: pageType,
    brand,
    ...(shouldIncludeSiteHierarchy && siteSection ? { site_section: siteSection } : {}),
    // Always include site_subsection key so GA4 receives an explicit `undefined`
    // when no subsection applies (avoids skewed (not set) reports per QC feedback).
    site_subsection: shouldIncludeSiteHierarchy ? siteSubsection : undefined,
  };
}

function getSiteSections(path: string, contentType: string): [string, string | undefined] {
  const segments = path.replaceAll(/(?:^\/$)/g, '').split('/');
  const pathSegment = segments[1];
  const contentTypeKey = contentType.toLowerCase();
  const pathKey = pathSegment?.toLowerCase();

  const siteSection =
    SITE_SECTION_MAP[contentTypeKey] ??
    (pathKey && pathSegment ? (SITE_SECTION_MAP[pathKey] ?? capitalize(pathSegment)) : 'Home');

  const hash = globalThis.window.location.hash.replaceAll('#', '');
  const normalizedHash = normalizeMapKey(hash);
  const siteSubsection = hash
    ? (SITE_SUBSECTION_MAP[normalizedHash] ?? capitalize(hash))
    : undefined;

  return [siteSection, siteSubsection];
}

function AnalyticsTracker({ contentType, locale }: Readonly<AnalyticsTrackerProps>) {
  const isGTMReady = useGTMReady();

  if (!isGTMReady) return null;

  return <AnalyticsTrackerInner contentType={contentType} locale={locale} />;
}

function AnalyticsTrackerInner({ contentType, locale }: Readonly<AnalyticsTrackerProps>) {
  usePageView(
    analytics,
    {
      pageType: contentType,
      brand: 'Guinness',
      siteType: 'Brand Site',
    },
    locale,
    'North America',
    (pageInfo: Record<string, unknown>) => {
      const { path } = pageInfo as { path: string };
      const [siteSection, siteSubsection] = getSiteSections(path, contentType);
      const pageType = getPageType(contentType, siteSection, siteSubsection);

      analytics.pushEvent(
        'page_view',
        buildPageViewPayload({
          locale,
          siteSection,
          siteSubsection,
          siteType: 'Ecommerce',
          pageType,
          brand: 'Guinness',
          region: 'North America',
        }),
      );
    },
  );
  useOutboundLink(analytics);
  useScrollPercentage(analytics);
  useStandardClick(analytics);
  useAnyRoadAnalytics(analytics, ECOMMERCE_BASE_INFO);
  useOpenTableAnalytics(analytics);
  useGatewayStatusUserProperty();

  return null;
}

export default AnalyticsTracker;

type DataLayerEvent = { event?: string; gateway_status_user?: string };

/**
 * GA4 user-scoped custom dimension `gateway_status_user` was being reported as
 * `(not set)` for the majority of events because the bedrock age-gate hook only
 * pushes it once on the `gateway` event itself (and not at all for returning
 * users with a valid age-gate cookie).
 *
 * This hook resolves it from the cookie on mount and also watches the dataLayer
 * for any subsequent `gateway` events, mirroring the latest value into a
 * dedicated `user_properties` push so GA4 picks it up as a user property and
 * carries it forward across all subsequent events.
 */
function useGatewayStatusUserProperty(): void {
  useEffect(() => {
    const win = globalThis as typeof globalThis & {
      dataLayer?: Record<string, unknown>[];
    };
    if (!win.dataLayer) return;

    let lastPushed: string | undefined;

    const pushUserProperty = (status: string | undefined): void => {
      if (!status || status === lastPushed) return;
      lastPushed = status;
      win.dataLayer?.push({
        event: 'set_user_properties',
        user_properties: { gateway_status_user: status },
      });
    };

    const readFromCookie = (): string | undefined => {
      const match = /(?:^|;\s*)age_gate=(?<value>[^;]+)/.exec(document.cookie);
      const value = match?.groups?.value;
      if (!value) return undefined;
      try {
        const parsed = JSON.parse(decodeURIComponent(value)) as { pass?: boolean };
        if (parsed.pass === true) return 'Completed';
        if (parsed.pass === false) return 'Rejected';
      } catch {
        return undefined;
      }
      return undefined;
    };

    const readLatestFromDataLayer = (): string | undefined => {
      const dataLayer = win.dataLayer ?? [];
      for (let i = dataLayer.length - 1; i >= 0; i--) {
        const ev = dataLayer[i] as DataLayerEvent;
        if (ev.event === 'gateway' && typeof ev.gateway_status_user === 'string') {
          return ev.gateway_status_user;
        }
      }
      return undefined;
    };

    pushUserProperty(readLatestFromDataLayer() ?? readFromCookie());

    const intervalId = setInterval(() => {
      pushUserProperty(readLatestFromDataLayer() ?? readFromCookie());
    }, 500);

    return () => {
      clearInterval(intervalId);
    };
  }, []);
}

const GTM_EVENTS = ['gtm.js', 'gtm.dom', 'gtm.load'] as const;

function areGTMEventsLoaded(dataLayer: DataLayerEvent[]): boolean {
  return GTM_EVENTS.every((name) => dataLayer.some((ev) => ev.event === name));
}

export function useGTMReady(): boolean {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const dataLayer = (globalThis as typeof globalThis & { dataLayer?: DataLayerEvent[] })
      .dataLayer;

    const intervalId = setInterval(() => {
      if (!dataLayer) return;

      if (areGTMEventsLoaded(dataLayer)) {
        setIsReady(true);
        clearInterval(intervalId);
      }
    }, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return isReady;
}
